<template>
    <div class="ComponentRangePaginator w-full">
        <input
            id="Pages"
            type="range"
            :aria-label="localizations[lang].tooltips.pageSlider"
            :aria-valuemin="1"
            :aria-valuemax="max"
            :aria-valuenow="position || currentPage"
            :value="position || currentPage"
            min="1"
            :max="max"
            :step="stepBy"
            :class="[
                'appearance-none',
                'bg-transparent',
                'h-6',
                'w-full',
                'cursor-pointer',
                'transition-all',
                'focus:outline-none',
                'focus-visible:ring-2',
                'focus-visible:ring-blue-500',
                'focus-visible:ring-offset-2',
                { isTouching },
            ]"
            :style="valuePercent"
            @input="selectedPage"
            @mouseup="selectSpine"
            @touchend="selectSpine"
        />
    </div>
</template>
<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import throttle from 'lodash/throttle';

export default {
    name: 'ComponentRangePaginator',
    data() {
        return {
            position: null,
            isTouching: false,
        };
    },
    computed: {
        ...mapState(['epub', 'localizations', 'lang']),
        ...mapGetters(['currentPage', 'publicationNumberOfPages', 'doublePageMode', 'isEpub', 'isPdf']),
        stepBy() {
            if (this.isEpub) {
                return 1;
            }

            return this.doublePageMode ? 2 : 1;
        },
        max() {
            if (this.isEpub) {
                // R2: use totalPositions (position-based navigation)
                // Fallback to spine.length for legacy R1
                return this.epub.totalPositions || this.epub.spine.length;
            }
            return this.publicationNumberOfPages;
        },
        valuePercent() {
            const position = this.position || this.currentPage;
            return {
                '--valuePercent': `${((position - 1) / (this.max - 1)) * 100}%`,
            };
        },
    },
    watch: {
        // Sync `position` with `currentPage` to maintain consistent pagination state.
        currentPage(newVal) {
            this.position = newVal;
        },
    },
    methods: {
        ...mapMutations(['rangePageSelect']),
        ...mapActions(['goToPage', 'goToLocation']),
        throttledGoToPage: throttle(
            function (selectedPage) {
                this.isTouching = false;
                if (this.isEpub) {
                    // R2: navigate by position index
                    if (this.epub.totalPositions) {
                        this.goToLocation({ epubR2PositionIndex: selectedPage - 1 });
                    } else {
                        // Legacy R1 fallback via spine
                        this.goToLocation({ chapterHref: this.epub.spine[selectedPage - 1].href });
                    }
                } else {
                    this.goToPage({ page: selectedPage });
                }
            },
            1000,
            {
                leading: true,
                trailing: true,
            }
        ),
        selectedPage(event) {
            this.setPosition(event);
            if (!this.isPdf) return;
            this.goToPageOrLocation();
        },
        selectSpine(event) {
            this.setPosition(event);
            if (!this.isEpub) return;
            this.goToPageOrLocation();
        },
        setPosition(event) {
            this.isTouching = true;
            const position = parseInt(event.target.value);
            if (this.position === position) return;
            this.position = position;
        },
        goToPageOrLocation() {
            this.rangePageSelect({ page: this.position });
            this.throttledGoToPage(this.position);
        },
    },
};
</script>
